"use client";

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SidebarContext = createContext(null);

const STORAGE_KEY = 'muster_sidebar_collapsed';

export function SidebarProvider({ children }) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        setIsCollapsed(stored === 'true');
      }
    } catch {
      // localStorage not available
    }
    setHydrated(true);
  }, []);

  // Persist collapse state
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(isCollapsed));
    } catch {
      // localStorage not available
    }
  }, [isCollapsed, hydrated]);

  // Update body class for CSS padding
  useEffect(() => {
    if (!hydrated) return;
    document.body.classList.toggle('sidebar-expanded', !isCollapsed);
  }, [isCollapsed, hydrated]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      if (next) setUserMenuOpen(false);
      return next;
    });
  }, []);

  const toggleUserMenu = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
  }, []);

  const closeUserMenu = useCallback(() => {
    setUserMenuOpen(false);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        userMenuOpen,
        hydrated,
        toggleCollapse,
        toggleUserMenu,
        closeUserMenu,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
};
